import { useCallback, useEffect, useState } from "react";
import { CalendarClock, Loader2, PauseCircle, RefreshCw, Store, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { fetchIfoodOpeningHours, fetchInterruptions, fetchMerchantDetails, removeInterruption } from "../api";

function fmtPeriodo(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return String(iso);
  }
}

function Linha({ rotulo, valor }) {
  if (!valor) return null;
  return (
    <div className="flex gap-2 text-[13px]">
      <span className="text-muted-foreground shrink-0">{rotulo}</span>
      <span className="font-medium break-words">{valor}</span>
    </div>
  );
}

/** Loja no iFood: cadastro, disponibilidade, horários e pausas ativas.
 *  É o espelho do que está lá — quem muda horário é Configurações → Horários,
 *  e quem pausa é o Fechar loja do cabeçalho. Aqui só se remove uma pausa. */
export default function LojaIfoodCard({ semModuloLoja }) {
  const [dados, setDados] = useState(null);
  const [horarios, setHorarios] = useState([]);
  const [pausas, setPausas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [removendo, setRemovendo] = useState("");

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [d, h, p] = await Promise.all([
        fetchMerchantDetails().catch(() => null),
        fetchIfoodOpeningHours().catch(() => ({ shifts: [] })),
        fetchInterruptions().catch(() => []),
      ]);
      setDados(d);
      setHorarios((h && h.shifts) || []);
      setPausas(Array.isArray(p) ? p : []);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const remover = async (id) => {
    setRemovendo(id);
    try {
      await removeInterruption(id);
      toast.success("Pausa removida no iFood");
      await carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível remover a pausa");
    } finally {
      setRemovendo("");
    }
  };

  if (semModuloLoja) {
    return (
      <div className="rounded-xl border bg-background p-4 text-sm text-muted-foreground">
        <p className="font-semibold text-foreground flex items-center gap-2"><Store className="w-4 h-4" /> Loja no iFood</p>
        <p className="mt-1 text-[13px] leading-relaxed">
          O iFood ainda não liberou o módulo de Loja para o Núcleo. Enquanto isso, os dados da loja, os horários e
          as pausas ficam no Portal do Parceiro.
        </p>
      </div>
    );
  }

  const detalhes = dados?.details || {};
  const endereco = detalhes.address
    ? [detalhes.address.street, detalhes.address.number, detalhes.address.neighborhood, detalhes.address.city]
        .filter(Boolean).join(", ")
    : "";

  return (
    <div className="rounded-xl border bg-background p-4 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold flex items-center gap-2"><Store className="w-4 h-4" /> Loja no iFood</p>
        <Button size="sm" variant="ghost" onClick={carregar} disabled={carregando}>
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${carregando ? "animate-spin" : ""}`} /> Atualizar
        </Button>
      </div>

      {carregando && !dados ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : (
        <>
          <div className="space-y-1">
            <Linha rotulo="Nome" valor={detalhes.name} />
            <Linha rotulo="Razão social" valor={detalhes.corporateName} />
            <Linha rotulo="Endereço" valor={endereco} />
            <Linha rotulo="Telefone" valor={detalhes.phone?.commercialNumber || detalhes.phone} />
            <Linha rotulo="ID" valor={detalhes.id} />
          </div>

          <div>
            <p className="text-[13px] font-semibold mb-1">Disponibilidade agora</p>
            {(dados?.operations || []).length === 0 ? (
              <p className="text-[13px] text-muted-foreground">O iFood não informou a disponibilidade.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {dados.operations.map((op) => (
                  <span
                    key={op.operation}
                    className={`text-[11px] px-2.5 py-1 rounded-full font-semibold ${
                      op.available ? "bg-green-500/15 text-green-600 dark:text-green-400" : "bg-amber-500/15 text-amber-700 dark:text-amber-400"
                    }`}
                    title={op.message || ""}
                  >
                    {op.operation}: {op.available ? "recebendo pedidos" : op.message || "indisponível"}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="text-[13px] font-semibold mb-1 flex items-center gap-1.5">
              <CalendarClock className="w-3.5 h-3.5" /> Horários no iFood
            </p>
            {horarios.length === 0 ? (
              <p className="text-[13px] text-muted-foreground">Nenhum turno cadastrado no iFood.</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-0.5">
                {horarios.map((t) => (
                  <div key={t.id || `${t.day}-${t.start}`} className="flex justify-between text-[13px]">
                    <span className="text-muted-foreground">{t.day_name}</span>
                    <span className="font-medium tabular-nums">{t.start} – {t.end}</span>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground mt-1">
              Salvar em Configurações → Horários substitui esta lista no iFood.
            </p>
          </div>

          <div>
            <p className="text-[13px] font-semibold mb-1 flex items-center gap-1.5">
              <PauseCircle className="w-3.5 h-3.5" /> Pausas ativas
            </p>
            {pausas.length === 0 ? (
              <p className="text-[13px] text-muted-foreground">Nenhuma pausa ativa — a loja segue o horário.</p>
            ) : (
              <div className="space-y-1.5">
                {pausas.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-2 rounded-lg bg-muted/40 px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium truncate">{p.description || "Pausa"}</p>
                      <p className="text-[11px] text-muted-foreground tabular-nums">
                        {fmtPeriodo(p.start)} até {fmtPeriodo(p.end)}
                      </p>
                    </div>
                    <Button
                      size="sm" variant="ghost"
                      className="shrink-0 text-red-600 hover:text-red-700 hover:bg-red-500/10"
                      onClick={() => remover(p.id)} disabled={removendo === p.id}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Remover
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
